<template>
  <main
    class="page"
    role="main"
  >
    <header
      class="page-header"
      aria-labelledby="teacher-infos-title"
    >
      <h1
        id="teacher-infos-title"
        class="h1"
      >
        Hinweise
      </h1>
      <div class="actions">
        <button
          class="btn btn-primary"
          @click="openNew"
        >
          Neu
        </button>
      </div>
    </header>

    <section class="card">
      <div class="form filters">
        <div class="field mb-0">
          <label for="q">Schülersuche</label>
          <input
            id="q"
            v-model="q"
            type="text"
            placeholder="Schüler oder Text"
            @input="applyFilter"
          >
        </div>
        <button
          type="button"
          class="btn btn-primary d-inline"
          :disabled="!q"
          aria-label="Filter zurücksetzen"
          @click="resetFilter"
        >
          Reset
        </button>
      </div>

      <div
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </div>

      <div
        v-if="loading"
        class="mt-12"
      >
        Lade…
      </div>

      <div
        v-else
        class="infos-table mt-12"
      >
        <table
          class="compact-table"
          style="width:100%; border-collapse: collapse;"
        >
          <thead>
            <tr class="table-rows">
              <th class="muted">
                <span
                  class="sortable"
                  role="button"
                  tabindex="0"
                  @click="setSort('studentName')"
                  @keydown.enter="setSort('studentName')"
                >
                  Schüler
                  <span v-if="sortKey === 'studentName'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </span>
              </th>
              <th class="muted">
                <span
                  class="sortable"
                  role="button"
                  tabindex="0"
                  @click="setSort('senderName')"
                  @keydown.enter="setSort('senderName')"
                >
                  Absender
                  <span v-if="sortKey === 'senderName'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </span>
              </th>
              <th class="muted">
                <span
                  class="sortable"
                  role="button"
                  tabindex="0"
                  @click="setSort('createdAt')"
                  @keydown.enter="setSort('createdAt')"
                >
                  gesendet
                  <span v-if="sortKey === 'createdAt'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </span>
              </th>
              <th class="muted">
                Gültig am
              </th>
              <th class="muted">
                Nachricht
              </th>
              <th class="muted">
                Gelesen
              </th>
              <th
                v-if="isTeacherOrAdmin || isParent"
                class="muted"
                style="text-align: right;"
              >
                Aktion
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in sorted"
              :key="item.id"
              class="table-rows"
              :style="{ fontWeight: isTeacherOrAdmin && !item.readAt ? '600' : '400' }"
            >
              <td>{{ item.studentName }}</td>
              <td>{{ item.senderName }}</td>
              <td>{{ formatDateTime(item.createdAt) }}</td>
              <td>
                {{ formatDateOnly(item.validOn || item.valid_on) }}
              </td>
              <td :title="item.message">
                {{ truncate(item.message, 40) }}
              </td>
              <td>
                <span
                  v-if="item.readAt"
                  :title="formatDate(item.readAt)"
                >
                  {{ item.readByUserName || '–' }}
                </span>
                <span v-else>–</span>
              </td>
              <td
                v-if="isTeacherOrAdmin || isParent"
                style=" text-align:right"
              >
                <button
                  v-if="isTeacherOrAdmin && !item.readAt"
                  class="icon-btn"
                  title="als gelesen markieren"
                  @click="markAsRead(item)"
                >
                  <i
                    class="pi pi-check"
                    aria-hidden="true"
                  />
                </button>
                <button
                  v-if="isTeacherOrAdmin && isOwner(item)"
                  class="icon-btn"
                  title="Hinweis bearbeiten"
                  style="margin-left:8px"
                  @click="openEdit(item)"
                >
                  <i
                    class="pi pi-pencil"
                    aria-hidden="true"
                  />
                </button>
                <button
                  v-else
                  class="icon-btn"
                  title="Hinweis ansehen"
                  style="margin-left:8px"
                  @click="openView(item)"
                >
                  <i
                    class="pi pi-eye"
                    aria-hidden="true"
                  />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile: Cards statt Tabelle -->
      <div
        v-if="!loading"
        class="infos-cards mt-12"
      >
        <div
          v-for="item in sorted"
          :key="`info-card-${item.id}`"
          class="info-card"
          :style="{ borderColor: (isTeacherOrAdmin && !item.readAt) ? 'color-mix(in srgb, var(--primary) 35%, var(--border))' : 'var(--border)' }"
        >
          <div class="info-card__top">
            <div class="info-card__title">
              <strong class="info-card__student">{{ studentFirstName(item) }}</strong>

              <span
                v-if="item.readAt"
                class="badge badge-success info-card__badge"
                title="Gelesen"
              >gelesen</span>
            </div>
            <div
              class="info-card__message"
              :title="item.message"
            >
              {{ item.message || '—' }}
            </div>
            <div class="meta">
              <span><span class="muted font-semibold">für: </span>{{
                formatDateOnly(item.validOn || item.valid_on)
              }}</span>
            </div>
          </div>

          <div class="info-card__meta">
            <div class="meta">
              <span><span class="muted font-semibold">Gesendet: </span>{{ formatDate(item.createdAt) }}</span>
              <span class="d-block"><span class="muted font-semibold">von: </span>{{ item.senderName }}</span>
            </div>
            <div class="info-card__actions">
              <button
                class="btn btn-primary d-inline"
                title="Hinweis ansehen"
                @click="openView(item)"
              >
                öffnen
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination (analog Groups) -->
      <div
        class="mt-12"
        style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
      >
        <span
          class="muted pagination-item"
          style="margin-right:auto"
        >Gesamt: {{ total }}</span>
        <label
          class="muted pagination-item"
          for="ti-limit"
        >pro Seite:</label>
        <select
          id="ti-limit"
          class="pagination-item"
          :value="limit"
          @change="changeLimit"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>
        <button
          class="btn"
          :disabled="page<=1"
          @click="prevPage"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ pages || 1 }}</span>
        <button
          class="btn"
          :disabled="page>=pages"
          @click="nextPage"
        >
          Weiter
        </button>
      </div>
    </section>

    <BaseDialog
      :open="openDialog"
      :disabled="saving"
      @close="closeDialog"
    >
      <template #title>
        {{ isReadonly ? 'Hinweis ansehen' : (isEditing ? 'Hinweis bearbeiten' : 'Hinweis erstellen') }}
      </template>
      <div class="form">
        <div
          v-if="!isEditing && !isReadonly"
          class="field"
        >
          <label for="studentId">Schüler</label>
          <select
            id="studentId"
            v-model="form.student_id"
          >
            <option
              :value="null"
              disabled
            >
              Bitte auswählen…
            </option>
            <option
              v-for="s in studentsSorted"
              :key="s.id"
              :value="s.id"
            >
              {{ s.label }}
            </option>
          </select>
        </div>
        <div
          v-if="!isEditing && !isReadonly"
          class="field"
        >
          <label for="validOn">Gültig am</label>
          <Calendar
            id="validOn"
            v-model="validOnAt"
            :manual-input="false"
            date-format="dd.mm.yy"
            :show-icon="true"
            :touch-u-i="true"
          />
        </div>

        <!-- Readonly: Aufbau wie Meldungen-View (.meta / .meta-row als Grid) -->
        <div
          v-if="isReadonly"
          class="meta"
        >
          <div class="meta-row">
            <span class="label">Schüler:</span>
            <span class="value">{{ currentItem?.studentName || '–' }}</span>
          </div>
          <div class="meta-row">
            <span class="label">Gültig am:</span>
            <span class="value">{{ formatDateOnly(currentItem?.validOn || currentItem?.valid_on) }}</span>
          </div>
          <div class="meta-row">
            <span class="label">gesendet am:</span>
            <span class="value">{{ currentItem?.createdAt ? formatDate(currentItem.createdAt) : '–' }}</span>
          </div>
          <div class="meta-row">
            <span class="label">Absender:</span>
            <span class="value">{{ currentItem?.senderName || '–' }}</span>
          </div>
          <div class="meta-row">
            <span class="label">Gelesen durch:</span>
            <span class="value">
              <span v-if="currentItem?.readAt">
                {{ currentItem?.readByUserName || '–' }} ({{ formatDate(currentItem.readAt) }})
              </span>
              <span v-else>–</span>
            </span>
          </div>
        </div>

        <div
          v-if="!isReadonly"
          class="field"
        >
          <label for="message">Nachricht</label>
          <textarea
            id="message"
            v-model="form.message"
            rows="4"
          />
        </div>
        <div
          v-else
          class="meta"
        >
          <p class="message">
            {{ form.message }}
          </p>
        </div>

        <div
          class="actions"
          style="display:flex; gap:8px; justify-content:flex-end;"
        >
          <button
            class="btn"
            :disabled="saving"
            @click="closeDialog"
          >
            {{
              isReadonly ? 'Schließen' : 'Abbrechen'
            }}
          </button>
          <button
            v-if="!isReadonly"
            class="btn btn-primary"
            :disabled="saving"
            @click="submit"
          >
            <span
              v-if="saving"
              class="spinner"
              aria-hidden="true"
            /> Speichern
          </button>
        </div>
        <p
          v-if="!isReadonly && saveError"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ saveError }}
        </p>
        <p
          v-if="!isReadonly && saveOk"
          class="notice notice-success mt-12"
          role="status"
        >
          Gespeichert.
        </p>
      </div>
    </BaseDialog>

    <HintViewDialog
      :open="viewOpen"
      :hint="currentItem || {}"
      @close="closeHintDialog"
      @marked-read="onHintMarkedRead"
    />
  </main>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import api from '@/api/teacherInfos';
import {useTeacherInfosStore} from '@/stores/teacherInfosStore';
import {useAuthStore} from '@/stores/auth';
import BaseDialog from '@/components/BaseDialog.vue';
import HintViewDialog from '@/components/hints/HintViewDialog.vue';
import http from '@/services/api';
import Calendar from 'primevue/calendar';
import {useToast} from 'primevue/usetoast';

const auth = useAuthStore();
const isTeacherOrAdmin = computed(() => auth.isAdmin === true || auth.isTeacher === true);
const isParent = computed(() => auth.isParent === true);

const route = useRoute();
const router = useRouter();

// Readonly-Dialogmodus (für "Ansehen")
const isReadonly = ref(false);

// Besitzerermittlung
const currentUserId = computed(() => auth.user?.id ?? auth.user?.userId ?? auth.user?.user_id ?? null);

function getOwnerId(item) {
  return item?.senderUserId ?? item?.senderId ?? item?.sender_id ?? item?.userId ?? item?.user_id ?? item?.createdBy ?? item?.created_by ?? item?.sender?.id ?? null;
}

function isOwner(item) {
  const ownerId = getOwnerId(item);
  return currentUserId.value != null && ownerId != null && Number(ownerId) === Number(currentUserId.value);
}

const items = ref([]);
const loading = ref(false);
const error = ref(null);
const q = ref('');
const onlyUnread = ref(false);

// Pagination
const page = ref(1);
const limit = ref(20);
const total = ref(0);
const pages = ref(0);

const openDialog = ref(false);
const isEditing = ref(false);
const currentItem = ref(null);
const form = ref({student_id: null, message: '', valid_on: ''});
const saving = ref(false);
const saveError = ref(null);
const saveOk = ref(false);

// View-Dialog (HintViewDialog)
const viewOpen = ref(false);

function closeHintDialog() {
  viewOpen.value = false;
  // Readonly-Zustand zurücksetzen, falls zuvor gesetzt
  isEditing.value = false;
  isReadonly.value = false;
}

async function onHintMarkedRead() {
  try {
    await load();
    await store.refreshUnread();
  } catch {
    /* ignore */
  }
}

const store = useTeacherInfosStore();
const toast = useToast();

// Schülerliste (aktiv) für den Dialog
const students = ref([]);
const studentsLoading = ref(false);
const studentsError = ref(null);

const studentsSorted = computed(() => {
  const arr = (students.value || []).map(s => {
    const first = String(s.first_name ?? s.firstName ?? '').trim();
    const last = String(s.last_name ?? s.lastName ?? '').trim();
    const name = [first, last].filter(Boolean).join(' ').trim();
    const label = name;
    return {id: s.id, label, sortKey: name};
  });
  arr.sort((a, b) => String(a.sortKey).localeCompare(String(b.sortKey), 'de', {sensitivity: 'base'}));
  return arr;
});

async function loadStudents() {
  if (students.value.length > 0) return;
  studentsLoading.value = true;
  studentsError.value = null;
  try {
    const {data} = await http.get('/students/all');
    // API kann entweder {items: [...]} oder direkt ein Array liefern
    const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
    // Nur aktive Einträge behalten (true/1/'1'/'true'); wenn Feld fehlt, als aktiv behandeln
    students.value = items.filter((s) => {
      if (!s) return false;
      const active = s.active;
      const status = s.status;
      const isActive = active === true || active === 1 || active === '1' || String(active).toLowerCase() === 'true' || typeof active === 'undefined';
      const isStatusActive = status === true || status === 1 || status === '1' || String(status).toLowerCase() === 'true' || typeof status === 'undefined';
      return isActive && isStatusActive;
    });
  } catch (e) {
    studentsError.value = e?.message || 'Schüler konnten nicht geladen werden.';
    students.value = [];
  } finally {
    studentsLoading.value = false;
  }
}

// PrimeVue-Calendar für "Gültig am"
const validOnAt = ref(null);

function toIsoDate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

watch(validOnAt, (val) => {
  form.value.valid_on = val instanceof Date ? toIsoDate(val) : '';
});

// Gefilterte Liste
const filtered = computed(() => {
  const query = q.value.trim().toLowerCase();
  return items.value.filter(it => {
    const matchesText = !query
        || it.studentName.toLowerCase().includes(query)
        || it.senderName.toLowerCase().includes(query)
        || it.message.toLowerCase().includes(query);
    const matchesUnread = !onlyUnread.value || !it.readAt;
    return matchesText && matchesUnread;
  });
});

// Sortierung wie in Groups.vue
const sortKey = ref('createdAt');
const sortDir = ref('desc');

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    // Datum standardmäßig absteigend; Textspalten aufsteigend
    sortDir.value = key === 'createdAt' ? 'desc' : 'asc';
  }
}

const sorted = computed(() => {
  const arr = [...filtered.value];

  const isUnread = (it) => !it?.readAt;

  arr.sort((a, b) => {
    // 1) Ungelesen immer nach oben
    const au = isUnread(a) ? 1 : 0;
    const bu = isUnread(b) ? 1 : 0;
    if (au !== bu) return bu - au;

    // 2) Innerhalb der Gruppen wie bisher sortieren
    let va, vb;
    switch (sortKey.value) {
      case 'studentName':
        va = String(a.studentName || '');
        vb = String(b.studentName || '');
        break;
      case 'senderName':
        va = String(a.senderName || '');
        vb = String(b.senderName || '');
        break;
      case 'createdAt':
      default:
        va = new Date(a.createdAt).getTime() || 0;
        vb = new Date(b.createdAt).getTime() || 0;
        break;
    }

    let result;
    if (typeof va === 'number' && typeof vb === 'number') {
      result = va - vb;
    } else {
      result = String(va).localeCompare(String(vb), 'de', {sensitivity: 'base'});
    }
    return sortDir.value === 'asc' ? result : -result;
  });

  return arr;
});

function formatDate(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const pad = (n) => String(n).padStart(2, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  } catch {
    return iso;
  }
}

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(String(iso));
    return d.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return String(iso);
  }
}

// function formatDateTime(iso) {
//   try {
//     const d = new Date(iso);
//     if (isNaN(d.getTime())) return iso;
//     const pad = (n) => String(n).padStart(2, '0');
//     const day = pad(d.getDate());
//     const month = pad(d.getMonth() + 1);
//     const year = d.getFullYear();
//     return `${day}.${month}.${year}`;
//   } catch {
//     return iso;
//   }
// }

function formatDateOnly(ymd) {
  if (!ymd) return '–';
  const s = String(ymd);
  // Erwartet YYYY-MM-DD
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  const [, y, mo, d] = m;
  return `${d}.${mo}.${y}`;
}

function truncate(text, max = 40) {
  if (text == null) return '';
  const str = String(text);
  const limit = Math.max(1, Number(max) || 40);
  if (str.length <= limit) return str;
  return str.slice(0, limit - 1) + '…';
}

async function load() {
  loading.value = true;
  error.value = null;
  try {
    const params = {
      page: page.value,
      limit: limit.value,
      q: q.value?.trim() || undefined,
      unread: onlyUnread.value ? '1' : undefined,
    };
    const data = await api.list(params);
    items.value = data?.items || [];
    total.value = data?.total ?? (Array.isArray(items.value) ? items.value.length : 0);
    pages.value = data?.pages ?? Math.max(1, Math.ceil((total.value || 0) / limit.value));
  } catch (e) {
    error.value = e?.message || 'Fehler beim Laden';
  } finally {
    loading.value = false;
  }
}

function applyFilter() {
  // Bei Filteränderung auf Seite 1 und neu laden
  page.value = 1;
  load();
}

function nextPage() {
  if (page.value < pages.value) {
    page.value += 1;
    load();
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value -= 1;
    load();
  }
}

function changeLimit(e) {
  const val = Number(e?.target?.value || limit.value);
  limit.value = val > 0 ? val : 20;
  page.value = 1;
  load();
}

function resetFilter() {
  q.value = '';
  applyFilter();
}


async function openNew() {
  await loadStudents();
  isEditing.value = false;
  isReadonly.value = false;
  currentItem.value = null;
  form.value = {student_id: null, message: '', valid_on: ''};
  validOnAt.value = new Date();
  saveError.value = null;
  saveOk.value = false;
  openDialog.value = true;
}

function openEdit(item) {
  // Nur Ersteller dürfen bearbeiten, alle anderen nur ansehen
  if (!isOwner(item)) {
    return openView(item);
  }
  isEditing.value = true;
  isReadonly.value = false;
  currentItem.value = item;
  form.value = {student_id: null, message: item?.message || ''};
  saveError.value = null;
  saveOk.value = false;
  openDialog.value = true;
}

function openView(item) {
  isEditing.value = false;
  isReadonly.value = true;
  currentItem.value = item;
  saveError.value = null;
  saveOk.value = false;
  viewOpen.value = true;
}

function closeDialog() {
  openDialog.value = false;
  isEditing.value = false;
  isReadonly.value = false;
  currentItem.value = null;
  saveError.value = null;
  saveOk.value = false;
  form.value = {student_id: null, message: '', valid_on: ''};
  validOnAt.value = null;
}

async function markAsRead(item) {
  try {
    const updated = await api.markRead(item.id);
    // Liste aktualisieren
    const idx = items.value.findIndex(x => x.id === item.id);
    if (idx >= 0) {
      items.value[idx] = updated;
    }
    // Unread-Count aktualisieren
    await store.refreshUnread();
  } catch (e) {
    error.value = e?.message || 'Fehler beim Aktualisieren';
  }
}

async function submit() {
  // Readonly-Ansicht: keine Speicherung
  if (isReadonly.value) {
    closeDialog();
    return;
  }

  saveError.value = null;
  saveOk.value = false;

  // Validierung
  if (!form.value.message?.trim()) {
    saveError.value = 'Bitte Nachricht angeben.';
    return;
  }
  if (!isEditing.value && !form.value.student_id) {
    saveError.value = 'Bitte Schüler-ID angeben.';
    return;
  }

  saving.value = true;
  try {
    if (isEditing.value && currentItem.value) {
      const updated = await api.update(currentItem.value.id, {message: form.value.message.trim()});
      const idx = items.value.findIndex(x => x.id === updated.id);
      if (idx >= 0) {
        items.value[idx] = {...items.value[idx], ...updated};
      }
    } else {
      const payload = {
        student_id: Number(form.value.student_id),
        message: form.value.message.trim(),
        valid_on: form.value.valid_on || undefined
      };
      await api.create(payload);
    }
    saveOk.value = true;
    openDialog.value = false;
    if (!isEditing.value) {
      form.value = {student_id: null, message: ''};
    }
    await load();
    await store.refreshUnread();
    toast.add({severity: 'success', summary: 'Gespeichert.', life: 3500});
  } catch (e) {
    saveError.value = e?.response?.data?.message || e?.message || 'Fehler beim Speichern';
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  await load();
  // Optional initial unread refresh (Navbar kann Store nutzen)
  try {
    await store.refreshUnread();
  } catch { /* ignore */
  }

  // Wenn mit ?new=1 aufgerufen wurde und Rolle erstellen darf → Dialog öffnen
  const wantsNew = route?.query?.new === '1' || route?.query?.new === 'true';
  if (wantsNew && (isTeacherOrAdmin.value || isParent.value)) {
    await openNew();
    // Query-Flag einmalig verbrauchen
    router.replace({name: route.name || 'TeacherInfos', query: {...route.query, new: undefined}});
  }
});

function studentFirstName(item) {
  const raw = item?.studentName ?? '';
  const s = String(raw || '').trim();
  if (!s) return '—';
  // "Nachname, Vorname" -> Vorname
  if (s.includes(',')) {
    const parts = s.split(',').map(x => x.trim()).filter(Boolean);
    if (parts[1]) return parts[1].split(/\s+/)[0] || parts[1];
  }
  // "Vorname Nachname" -> Vorname
  return s.split(/\s+/)[0] || s;
}
</script>

<style scoped>
:root {
  --text: #1F2937;
  --muted: #6B7280;
  --surface: #FFFFFF;
  --bg: #F6F7F9;
  --border: #E0E3EA;
  --primary: #4F46E5;
  --primary-600: #4338CA;
  --focus: rgba(79, 70, 229, .45);
}

.page {
  padding: 16px;
  background: var(--bg);
  color: var(--text);
}

.table-rows td, .table-rows th {
  padding: 5px 0;
  text-align: left;
  border-bottom: 1px solid var(--border)
}

.table-rows th {
  font-weight: 600;
}

.page-header {
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px;
}

.btn {
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-primary:hover {
  background: var(--primary-600);
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  display: inline-block;
  margin-right: 8px;
  animation: spin 1s linear infinite;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--surface);
}

.notice-danger {
  background: rgba(239, 68, 68, .1);
  border-color: #fecaca;
}

.notice-success {
  background: rgba(16, 185, 129, .1);
  border-color: #bbf7d0;
}

.muted {
  color: var(--muted);
}

.mt-12 {
  margin-top: 12px;
}

.form .field label {
  font-size: 12px;
  color: var(--muted);
  display: block;
  margin-bottom: 4px;
}

.form .field input, .form .field textarea, .form .field select {
  width: 100%;
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
}

.form .field textarea {
  height: auto;
}

.form .field input:focus, .form .field textarea:focus, .form .field select:focus {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

.compact-table th, .compact-table td {
  font-size: 12px;
  line-height: 20px;
}

.compact-table td {
  font-size: 14px;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  text-decoration: underline;
}

/* Dialog-Readonly: Meta-Block wie Meldungen-View */
.dialog-body .meta {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--surface);
  margin-bottom: 12px;
}

.meta-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px;
  align-items: center;
}

.label {
  color: var(--muted);
  font-size: 12px;
}

.value {
  color: var(--text);
  font-size: 14px;
}

.message {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
}

.filters {
  display: grid;
  grid-template-columns: auto 90px;
  align-items: end;
  gap: 6px;
  margin-bottom: 20px;
}

.infos-cards {
  display: none;
}

.info-card {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
  display: grid;
  gap: 10px;
  max-width: 100%;
  min-width: 0;

}

.info-card__top {
  display: block;
  max-width: 100%;
  min-width: 0;

}

.info-card__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.info-card__badge {
  flex: 0 0 auto;
  margin-left: auto;
  display: inline-block;
  font-size: 12px;
  line-height: 16px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
}

.info-card__badge.badge-success {
  color: var(--success);
  border-color: rgba(16, 185, 129, .5);
  background: rgba(16, 185, 129, .08);
}

.info-card__student {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info-card__actions {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.info-card__meta {
  display: grid;
  grid-template-columns: auto 90px;
  gap: 10px;
  align-items: end;
}

.info-card .meta {
  font-size: 12px;
  color: var(--muted);
}

.info-card__message {
  margin-bottom: 6px;
  font-size: 14px;
  line-height: 1.45;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--muted);
  max-width: 100%;
  min-width: 0;

}

@media (max-width: 799px) {
  .page .infos-table {
    display: none;
  }

  .page .filters {
    display: grid;
    grid-template-columns: auto 90px;
    align-items: end;
    gap: 6px;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px;
    background: whitesmoke;
    margin-bottom: 20px;
  }

  .page .filters input,
  .page .filters button {
    height: 32px;
    font-size: 14px;
    padding: 0 10px !important;
    width: 100%;
  }

  .page .pagination-item {
    display: none;
  }

  .page .infos-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 10px;
  }

  /* Meta-Grid im Dialog auf Mobile etwas kompakter */
  .meta-row {
    grid-template-columns: 110px 1fr;
  }
}
</style>
